import { useQuery, useQueryClient, UseQueryOptions } from 'react-query'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ArticleClient } from 'api/clients/ArticleClient'
import { layersKeys } from './layersKeys'
import { Layer } from 'types'
import { useArticlePassword } from 'hooks/useArticlePassword'

interface Props {
  articleKey: string
  id: string
  enabled?: boolean
  onSuccess?: (data: { layer: Layer }) => void
}

type CacheData = { layers: Layer[] } | undefined

export const useLayer = (
  { articleKey, id, enabled = true }: Props,
  options?: UseQueryOptions<
    { layer: Layer },
    unknown,
    { layer: Layer },
    (string | { password: string | null | undefined })[]
  >,
) => {
  const queryClient = useQueryClient()
  const password = useArticlePassword(articleKey)

  return useQuery(
    layersKeys.detail(id, password),
    async () => {
      const res = await ArticleClient.getLayer(articleKey, id, password)

      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }
      return res.data
    },
    {
      initialData: () => {
        const existingLayer = queryClient
          .getQueryData<CacheData>(layersKeys.list(articleKey, { password: password }))
          ?.layers.find((layer) => layer.id === id)

        if (!existingLayer) return

        return {
          layer: existingLayer,
        }
      },
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled,
      ...options,
    },
  )
}
